/**
 * The platform console (VCNet's, over every organisation -- not an
 * organisation's Admin role): organisations, their plans and mail pools, the
 * requests for more mail, platform admins, the invite list, accounts on or
 * off, and the record of every change made here. Ported from utucat's Admin
 * screen, as rows rather than tables so one layout holds at a phone's width.
 *
 * Reachable only by a platform admin (the `ADMIN_EMAILS` secret, or added
 * here); the worker answers anybody else with a 404, so hiding the link is a
 * courtesy, not the lock. Every lever here can be undone. An organisation can
 * be looked at, to support it -- its settings and usage, never its work -- and
 * each look is recorded.
 */

import { useEffect, useState, type FormEvent } from "react";
import {
  PLAN_IDS,
  PLANS,
  planOf,
  type AdminAccount,
  type AdminOrganization,
  type AdminOrganizationView,
  type AdminOverview,
} from "@utuwrk/core";
import { ApiError, api } from "../lib/api.js";
import { PageHeader } from "./bits.js";

const sentence = (problem: unknown): string =>
  problem instanceof ApiError ? problem.message : "Something went wrong. Try again.";

const day = (seconds: number): string =>
  new Date(seconds * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const when = (seconds: number): string =>
  new Date(seconds * 1000).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

const ACTION_LABEL: Record<string, string> = {
  "allowlist.add": "Invited",
  "allowlist.remove": "Uninvited",
  "account.disabled": "Disabled",
  "account.active": "Enabled",
  "organization.create": "Created organisation",
  "vault.reseal": "Re-sealed private values:",
  "organization.view": "Looked at",
  "organization.mail": "Mail allowance",
  "mail-request.open": "Asked for more mail:",
  "mail-request.grant": "Granted mail request",
  "mail-request.decline": "Declined mail request",
  "platform-admin.add": "Made platform admin",
  "platform-admin.remove": "Removed platform admin",
};

const count = (n: number): string => n.toLocaleString();

export function AdminConsole({ selfId, selfEmail }: { selfId: string; selfEmail: string }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Runs one write; the answer is the whole console again, drawn from what D1 now holds. */
  async function run(work: () => Promise<AdminOverview>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setOverview(await work());
      return true;
    } catch (problem) {
      setError(sentence(problem));
      return false;
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void run(api.admin.overview);
  }, []);

  if (!overview) {
    return (
      <section className="admin" aria-busy={!error}>
        <PageHeader title="Platform" meta={error ? null : "Loading…"} />
        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="admin">
      <PageHeader
        title="Platform"
        meta="VCNet's console over every organisation, not an organisation's own Admin role. Nothing here can see an organisation's work; every change, and every look at an organisation, is recorded at the bottom."
      />
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <MailRequests overview={overview} busy={busy} run={run} />
      <Organizations overview={overview} busy={busy} run={run} />
      <PlatformAdmins overview={overview} busy={busy} run={run} selfEmail={selfEmail} />
      <Invites overview={overview} busy={busy} run={run} />
      <Accounts overview={overview} busy={busy} run={run} selfId={selfId} />
      <EncryptionKey overview={overview} busy={busy} run={run} />
      <Recent overview={overview} />
    </section>
  );
}

interface PanelProps {
  overview: AdminOverview;
  busy: boolean;
  run: (work: () => Promise<AdminOverview>) => Promise<boolean>;
}

/** The plans by name, for the console's choices (PRD 13.2). */
const PLAN_CHOICES = PLAN_IDS.map((id) => ({ id, label: `${PLANS[id].name}, $${PLANS[id].price}` }));

function Organizations({ overview, busy, run }: PanelProps) {
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [plan, setPlan] = useState<string>("free");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await run(() => api.admin.createOrganization(name, adminEmail, plan))) {
      setName("");
      setAdminEmail("");
    }
  }

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Organisations</h2>
      <p className="muted">
        The first admin gets an account straight away and can sign in without being on the invite list.
      </p>
      <form className="admin-form" onSubmit={submit}>
        <label className="field">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Harbor Health" />
        </label>
        <label className="field">
          First admin
          <input
            type="email"
            value={adminEmail}
            onChange={(event) => setAdminEmail(event.target.value)}
            placeholder="ops@harborhealth.com"
          />
        </label>
        <label className="field">
          Plan
          <select value={plan} onChange={(event) => setPlan(event.target.value)}>
            {PLAN_CHOICES.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        <button className="button" type="submit" disabled={busy || !name.trim() || !adminEmail.trim()}>
          Create
        </button>
      </form>
      {overview.organizations.length === 0 ? (
        <p className="muted">No organisations yet.</p>
      ) : (
        <ul className="cards">
          {overview.organizations.map((organization) => (
            <li key={organization.id} className="card">
              <div className="card-top">
                <h3 className="card-title">{organization.name}</h3>
                <label className="field field-inline">
                  <span className="visually-hidden">Plan for {organization.name}</span>
                  <select
                    value={organization.plan}
                    disabled={busy}
                    onChange={(event) =>
                      void run(() => api.admin.setPlan(organization.id, event.target.value))
                    }
                  >
                    {PLAN_CHOICES.map((choice) => (
                      <option key={choice.id} value={choice.id}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <RenameOrganization id={organization.id} name={organization.name} busy={busy} run={run} />
              <MailAllowance organization={organization} busy={busy} run={run} />
              {organization.billing ? (
                <p className="card-meta">
                  <span
                    className={`pill ${organization.billing === "active" || organization.billing === "trialing" ? "s-sent" : "s-warn"}`}
                  >
                    Stripe: {organization.billing.replace("_", " ")}
                  </span>
                  <span>A change here is overwritten by Stripe's next event.</span>
                </p>
              ) : null}
              <p className="card-meta">
                <span>
                  {organization.members} {organization.members === 1 ? "member" : "members"}
                </span>
                <span className="admin-wrap">
                  Admin{organization.admins.length === 1 ? "" : "s"}:{" "}
                  {organization.admins.join(", ") || "none"}
                </span>
                <span>Created {day(organization.createdAt)}</span>
                <span className="admin-wrap">Id {organization.id}</span>
              </p>
              <OrganizationLook id={organization.id} name={organization.name} />
              <div className="admin-row">
                {organization.deleteAfter !== null ? (
                  <span className="pill s-danger">Closing: deleted {day(organization.deleteAfter)}</span>
                ) : (
                  <span />
                )}
                <button
                  className={`button ${organization.deleteAfter !== null ? "button-quiet" : "button-danger"}`}
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(() =>
                      organization.deleteAfter !== null
                        ? api.admin.reopen(organization.id)
                        : api.admin.close(organization.id),
                    )
                  }
                >
                  {organization.deleteAfter !== null ? "Reopen" : "Close"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RenameOrganization({
  id,
  name: current,
  busy,
  run,
}: Omit<PanelProps, "overview"> & { id: string; name: string }) {
  const [name, setName] = useState(current);

  async function submit(event: FormEvent) {
    event.preventDefault();
    await run(() => api.admin.renameOrganization(id, name));
  }

  return (
    <form className="admin-form" onSubmit={submit}>
      <label className="field">
        Name
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <button
        className="button button-quiet"
        type="submit"
        disabled={busy || !name.trim() || name.trim() === current}
      >
        Rename
      </button>
    </form>
  );
}

function Invites({ overview, busy, run }: PanelProps) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await run(() => api.admin.invite(email, note))) {
      setEmail("");
      setNote("");
    }
  }

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Invite list</h2>
      <p className="muted">
        Only these addresses can create an account. Removing one stops new sign-ups only; to lock out an
        existing account, disable it below.
      </p>
      <form className="admin-form" onSubmit={submit}>
        <label className="field">
          Address
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="someone@example.com"
          />
        </label>
        <label className="field">
          Note (optional)
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="HR lead, Harbor"
          />
        </label>
        <button className="button" type="submit" disabled={busy || !email.trim()}>
          Invite
        </button>
      </form>
      {overview.allowlist.length === 0 ? (
        <p className="muted">Nobody is invited yet.</p>
      ) : (
        <ul className="cards">
          {overview.allowlist.map((entry) => (
            <li key={entry.email} className="card">
              <div className="card-top">
                <h3 className="card-title">{entry.email}</h3>
                <span className={`pill ${entry.hasAccount ? "s-sent" : "s-waiting"}`}>
                  {entry.hasAccount ? "Has an account" : "Not signed in yet"}
                </span>
              </div>
              <div className="admin-row">
                <p className="card-meta">
                  {entry.note ? <span>{entry.note}</span> : null}
                  <span>
                    Added {day(entry.createdAt)} by {entry.addedBy}
                  </span>
                </p>
                <button
                  className="button button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={() => void run(() => api.admin.uninvite(entry.email))}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Accounts({ overview, busy, run, selfId }: PanelProps & { selfId: string }) {
  const toggle = (account: AdminAccount) =>
    run(() => api.admin.setStatus(account.id, account.status === "active" ? "disabled" : "active"));

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Accounts</h2>
      <p className="muted">
        Disabling takes effect on the account's next request and ends every session it holds. Nothing is
        deleted.
      </p>
      {overview.accounts.length === 0 ? (
        <p className="muted">Nobody has an account yet.</p>
      ) : (
        <ul className="cards">
          {overview.accounts.map((account) => (
            <li key={account.id} className="card">
              <div className="card-top">
                <h3 className="card-title">{account.email}</h3>
                <span className={`pill ${account.status === "active" ? "s-sent" : "s-danger"}`}>
                  {account.status === "active" ? "Active" : "Disabled"}
                </span>
              </div>
              <div className="admin-row">
                <p className="card-meta">
                  {account.organizations.map((organization) => (
                    <span key={organization.id} className="chip">
                      {organization.name} · {organization.role}
                    </span>
                  ))}
                  <span>Since {day(account.createdAt)}</span>
                </p>
                {account.id === selfId ? (
                  <span className="muted admin-self">You</span>
                ) : (
                  <button
                    className="button button-quiet"
                    type="button"
                    disabled={busy}
                    onClick={() => void toggle(account)}
                  >
                    {account.status === "active" ? "Disable" : "Enable"}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Rotating `VAULT_KEY` (deploying.md): with the new key set and the old one
 * kept as `VAULT_KEY_OLD`, this re-wraps every organisation's private values
 * under the new key. It hears back counts only. Nothing left under the old
 * key is what says the old one can go.
 */
function EncryptionKey({ overview, busy, run }: PanelProps) {
  const done = overview.resealed;
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Encryption key</h2>
      {overview.vaultRotating ? (
        <p className="muted">
          A rotation is under way: values sealed under the old key still open. Re-seal moves every
          organisation's private values to the new key without opening them; run it until nothing is left to
          move, then remove VAULT_KEY_OLD.
        </p>
      ) : (
        <p className="muted">
          To rotate the key that seals private values, set the new key as VAULT_KEY and the current one as
          VAULT_KEY_OLD (deploying.md), then re-seal here.
        </p>
      )}
      {done ? (
        <p className={done.unreadable > 0 ? "notice-line warn" : "pill s-sent"} role="status">
          {done.resealed} moved to the new key and {done.current} already on it, across {done.organizations}{" "}
          organisations.
          {done.unreadable > 0
            ? ` ${done.unreadable} would open under neither key; they were already lost and stay so.`
            : done.resealed === 0
              ? " Nothing is left under the old key: VAULT_KEY_OLD can be removed."
              : " Run it once more to confirm nothing is left."}
        </p>
      ) : null}
      <div className="action-row">
        <button
          type="button"
          className="button button-quiet"
          disabled={busy || !overview.vaultRotating}
          onClick={() => void run(api.admin.reseal)}
        >
          Re-seal under the new key
        </button>
      </div>
    </div>
  );
}

function Recent({ overview }: { overview: AdminOverview }) {
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Recent changes</h2>
      {overview.recent.length === 0 ? (
        <p className="muted">Nothing has been changed here yet.</p>
      ) : (
        <ul className="admin-log">
          {overview.recent.map((action) => (
            <li key={action.id}>
              <span className="admin-log-what">
                {ACTION_LABEL[action.action] ?? action.action}{" "}
                <span className="admin-wrap">{action.detail}</span>
              </span>
              <span className="muted admin-log-who">
                {action.actorEmail}, {when(action.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Organisations' asks for more mail, oldest first, with what each rests on. */
function MailRequests({ overview, busy, run }: PanelProps) {
  if (overview.mailRequests.length === 0) return null;
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Mail requests</h2>
      <p className="muted">Granting sets the organisation's hourly allowance to what it asked for.</p>
      <ul className="cards">
        {overview.mailRequests.map((ask) => (
          <li key={ask.id} className="card">
            <div className="card-top">
              <h3 className="card-title">{ask.organizationName}</h3>
              <span className="pill s-waiting">
                {count(ask.current)} → {count(ask.perHour)} an hour
              </span>
            </div>
            {ask.reason ? <p className="card-description">{ask.reason}</p> : null}
            <p className="card-meta">
              <span>Busiest hour this week: {count(ask.busiestHour)}</span>
              <span>Held back this week: {count(ask.heldThisWeek)}</span>
              <span className="admin-wrap">
                Asked by {ask.requestedBy}, {when(ask.createdAt)}
              </span>
            </p>
            <div className="action-row">
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.admin.decideMailRequest(ask.id, "grant"))}
              >
                Grant
              </button>
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.admin.decideMailRequest(ask.id, "decline"))}
              >
                Decline
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One organisation's hourly mail allowance: a figure of its own, or its plan's. */
function MailAllowance({
  organization,
  busy,
  run,
}: Omit<PanelProps, "overview"> & { organization: AdminOrganization }) {
  const [perHour, setPerHour] = useState(String(organization.mailPerHour));
  const fromPlan = planOf(organization.plan)?.mailPerHour;
  const whole = /^\d+$/.test(perHour) && Number(perHour) > 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    await run(() => api.admin.setMailAllowance(organization.id, Number(perHour)));
  }

  async function backToPlan() {
    if ((await run(() => api.admin.setMailAllowance(organization.id, null))) && fromPlan !== undefined)
      setPerHour(String(fromPlan));
  }

  return (
    <form className="admin-form" onSubmit={submit}>
      <label className="field">
        Mail an hour ({count(organization.mailLastHour)} sent in the last hour
        {organization.mailOverride === null ? "; the plan's figure" : ""})
        <input
          type="number"
          min={1}
          step={1}
          value={perHour}
          onChange={(event) => setPerHour(event.target.value)}
        />
      </label>
      <button
        className="button button-quiet"
        type="submit"
        disabled={busy || !whole || Number(perHour) === organization.mailPerHour}
      >
        Set
      </button>
      {organization.mailOverride !== null ? (
        <button
          className="button button-quiet"
          type="button"
          disabled={busy}
          onClick={() => void backToPlan()}
        >
          Back to the plan's{fromPlan !== undefined ? ` (${count(fromPlan)})` : ""}
        </button>
      ) : null}
    </form>
  );
}

/**
 * One organisation as support may see it: its people's accounts, structure,
 * templates by name, counts of its work and its mail. Loaded only on asking,
 * since each look is recorded.
 */
function OrganizationLook({ id, name }: { id: string; name: string }) {
  const [look, setLook] = useState<AdminOrganizationView | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    if (look) {
      setLook(null);
      return;
    }
    setError(null);
    try {
      setLook(await api.admin.viewOrganization(id));
    } catch (problem) {
      setError(sentence(problem));
    }
  }

  return (
    <div>
      <div className="action-row">
        <button className="button button-quiet" type="button" onClick={() => void toggle()}>
          {look ? `Hide ${name}` : `Look at ${name}`}
        </button>
      </div>
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}
      {look ? (
        <div className="admin-look">
          <p className="muted">Read-only, and never its people's work. This look is recorded below.</p>
          <h4 className="admin-subheading">Work</h4>
          <p className="card-meta">
            <span>{count(look.work.employees)} employees</span>
            <span>{count(look.work.openOnboardings)} onboardings open</span>
            <span>{count(look.work.openOffboardings)} offboardings open</span>
            <span>{count(look.work.finished)} finished</span>
            <span>
              Last activity:{" "}
              {look.work.lastActivityAt ? when(Math.floor(look.work.lastActivityAt / 1000)) : "none yet"}
            </span>
          </p>
          <h4 className="admin-subheading">Mail</h4>
          <p className="card-meta">
            <span>{count(look.mail.perHour)} an hour</span>
            <span>{count(look.mail.lastHour)} in the last hour</span>
            <span>Busiest hour this week: {count(look.mail.busiestHour)}</span>
            <span>Held back this week: {count(look.mail.heldThisWeek)}</span>
          </p>
          <h4 className="admin-subheading">People ({look.members.length})</h4>
          <ul className="admin-log">
            {look.members.map((person) => (
              <li key={person.email}>
                <span className="admin-log-what admin-wrap">
                  {person.name ? `${person.name}, ` : ""}
                  {person.email}
                </span>
                <span className="muted admin-log-who">
                  {person.role}
                  {person.department ? `, ${person.department}` : ""}
                  {person.active ? "" : ", off"}
                </span>
              </li>
            ))}
          </ul>
          <h4 className="admin-subheading">Companies and sites</h4>
          <p className="card-meta">
            {look.levels
              .filter((level) => level.kind !== "global")
              .map((level) => (
                <span key={`${level.kind}-${level.parent ?? ""}-${level.name}`} className="chip">
                  {level.kind === "site" && level.parent ? `${level.parent} · ` : ""}
                  {level.name}
                </span>
              ))}
          </p>
          <h4 className="admin-subheading">Departments</h4>
          <p className="card-meta">
            {look.departments.map((department) => (
              <span key={department} className="chip">
                {department}
              </span>
            ))}
          </p>
          <h4 className="admin-subheading">Its own templates</h4>
          {look.templates.length === 0 ? (
            <p className="muted">Only the starters.</p>
          ) : (
            <p className="card-meta">
              {look.templates.map((template) => (
                <span key={`${template.name}-${template.version}`} className="chip">
                  {template.name} · {template.kind} v{template.version}
                  {template.retired ? " · retired" : ""}
                </span>
              ))}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Platform admins: those on the ADMIN_EMAILS secret (a deploy's to change,
 * and the way back in if this list is ever emptied) and those added here.
 */
function PlatformAdmins({ overview, busy, run, selfEmail }: PanelProps & { selfEmail: string }) {
  const [email, setEmail] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await run(() => api.admin.addPlatformAdmin(email))) setEmail("");
  }

  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Platform admins</h2>
      <p className="muted">
        VCNet's people, who can open this console. Not an organisation's Admin role, which reaches only that
        organisation. Those on the ADMIN_EMAILS secret can only be removed by a deploy.
      </p>
      <form className="admin-form" onSubmit={submit}>
        <label className="field">
          Address
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="someone@vcnet.us"
          />
        </label>
        <button className="button" type="submit" disabled={busy || !email.trim()}>
          Add
        </button>
      </form>
      <ul className="admin-log">
        {overview.platformAdmins.map((person) => (
          <li key={person.email}>
            <span className="admin-log-what admin-wrap">{person.email}</span>
            {person.source === "secret" ? (
              <span className="muted admin-log-who">On the secret</span>
            ) : person.email === selfEmail.toLowerCase() ? (
              <span className="muted admin-log-who">You</span>
            ) : (
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => void run(() => api.admin.removePlatformAdmin(person.email))}
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
